import { Immutable } from "immer";
import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { loadWistiaPlayer } from "../../utils/wistia";

type IProps = Immutable<{
    videoId: string;
}>;

interface WistiaEmbed {
    remove?: () => void;
}

interface WistiaApi {
    embed: (videoId: string, options: unknown) => WistiaEmbed;
}

const CROP_FILL_PLUGIN = {
    plugin: {
        cropFill: {
            src: "https://fast.wistia.com/labs/crop-fill/plugin.js",
        },
    },
};

export const WistiaBackgroundVideo = (props: IProps): React.JSX.Element => {
    const videoId = props.videoId;

    // Embed the background video from this component's own effect, which runs
    // *after* React has hydrated the <figure> below, so the imperative Wistia
    // DOM mutation never races hydration. Injecting the player before React
    // hydrated this figure produced a hydration mismatch (React error #418) that
    // regenerated the subtree and discarded the video — observed as a blank hero
    // in Safari. See #34111.
    React.useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }
        const state: { cancelled: boolean; embed: WistiaEmbed | null } = {
            cancelled: false,
            embed: null,
        };
        void (async () => {
            try {
                await loadWistiaPlayer();
            } catch {
                return;
            }
            const wistia = window.Wistia as WistiaApi | undefined;
            if (state.cancelled || !wistia) {
                return;
            }
            state.embed = wistia.embed(videoId, CROP_FILL_PLUGIN);
        })();
        return () => {
            state.cancelled = true;
            state.embed?.remove?.();
        };
    }, [videoId]);

    return (
        <figure
            id={`wistia_${props.videoId}`}
            data-video-id={props.videoId}
            role="presentation"
            aria-hidden="true"
        ></figure>
    );
};
